/**
 * Forgot-password — email the user a reset link.
 *
 * Backend `/v1/auth/forgot-password` always returns sent=true so we
 * can't (and shouldn't) tell the user whether the email matched a
 * real account. The success copy is intentionally vague: "if that
 * email exists, a link is on its way." This is the standard anti-
 * enumeration pattern.
 */

import { Anchor, Button, Container, Group, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { useTurnstile } from "../lib/turnstile";

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const turnstile = useTurnstile();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (turnstile.enabled && turnstile.token === null) {
      setError("נא להמתין לסיום בדיקת הבוטים ולנסות שוב.");
      return;
    }
    setBusy(true);
    try {
      await api.forgotPassword(email, turnstile.token);
      setDone(true);
    } catch (err) {
      turnstile.reset();
      if (err instanceof ApiError && err.detail === "forgot_password_rate_limited") {
        setError("יותר מדי בקשות. נסה/י שוב בעוד כמה דקות.");
      } else if (err instanceof ApiError && err.detail === "turnstile_failed") {
        setError("בדיקת בוטים נכשלה. רענן את הדף ונסה/י שוב.");
      } else {
        setError("משהו השתבש. נסה/י שוב.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container size="xs" py="xl">
      <Paper p="lg" radius="md" withBorder>
        <Stack>
          <Title order={2}>שכחתי סיסמה</Title>
          {done ? (
            <>
              <Text>
                אם הכתובת רשומה אצלנו, שלחנו אליה קישור לאיפוס הסיסמה. הקישור
                תקף 15 דקות.
              </Text>
              <Text c="dimmed" size="sm">
                בדוק/י גם את תיבת ה-spam. אם לא הגיע, אפשר לבקש שוב בעוד דקה.
              </Text>
              <Group justify="center">
                <Anchor component={Link} to="/login" size="sm">
                  חזרה לכניסה
                </Anchor>
              </Group>
            </>
          ) : (
            <form onSubmit={submit}>
              <Stack>
                <Text c="dimmed" size="sm">
                  הקלד/י את כתובת הדוא"ל של החשבון שלך. נשלח אליך קישור לאיפוס
                  הסיסמה. הקישור תקף 15 דקות וניתן לשימוש פעם אחת.
                </Text>
                <TextInput
                  label="כתובת דוא״ל"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  dir="ltr"
                />
                {turnstile.enabled && (
                  <div ref={turnstile.containerRef} style={{ minHeight: 65 }} />
                )}
                {error && <Text c="red" size="sm">{error}</Text>}
                <Button
                  type="submit"
                  loading={busy}
                  fullWidth
                  disabled={
                    !email || (turnstile.enabled && turnstile.token === null)
                  }
                >
                  שלח קישור
                </Button>
                <Group justify="center">
                  <Anchor component={Link} to="/login" size="sm">
                    חזרה לכניסה
                  </Anchor>
                </Group>
              </Stack>
            </form>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}
